import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getIssueImage } from '../../api/aws/s3';
import { db } from '../../api/firebase/config';
import { companiesRef } from '../../api/firebase/firestore/company';
import { issuesRef } from '../../api/firebase/firestore/issue';
import {
  black,
  blackText,
  blueGrayText,
  primarycolor,
  secondaryButtonnBackground
} from '../../constants/Colors';
import { purpose } from '../../constants/Select';
import {
  negotiationStatus,
  screenType,
  select,
  status
} from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';
import noImage from '../assets/noImage.svg';

import type { Issue } from '../../api/firebase/firestore/issue/issueType';
import type { DocumentData } from 'firebase/firestore';

interface Props {
  issue: Issue;
  screen: number;
  companyStatus: number;
}

interface StatusCounts {
  [status: string]: number;
}

const styles = {
  card: {
    '@media screen and (max-width:960px)': {
      padding: '16px'
    },
    padding: '32px'
  },
  cardWrapper: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column'
    },
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    width: '100%'
  },
  cardcontent: {
    '&:last-child': {
      paddingBottom: 0
    },
    '@media screen and (max-width:960px)': {
      padding: '16px'
    },
    padding: '32px'
  },
  category: {
    color: blueGrayText,
    fontSize: '14px'
  },
  categoryTitle: {
    '@media screen and (max-width:480px)': { fontSize: '18px' },
    color: black,
    fontSize: '20px',
    fontWeight: 700,
    overflowWrap: 'anywhere'
  },
  checkbox: {
    '&.Mui-checked': {
      color: black
    },
    color: black
  },
  comparison: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'row',
      height: '40px',
      justifyContent: 'flex-end',
      padding: '8px 16px',
      width: '100%'
    },
    alignItems: 'center',
    bgcolor: '#DBDBE0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '48px'
  },
  container: {
    alignItems: 'center',
    display: 'flex'
  },
  copyButton: {
    color: primarycolor,
    marginLeft: '16px'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black,
    marginBottom: '8px',
    marginTop: '8px'
  },
  edit: {
    backGroundColor: 'white',
    border: `1px solid ${black}`,
    color: blackText,
    maxWidth: '61px',
    width: '100%'
  },
  editWrapper: {
    '@media screen and (max-width:480px)': {
      justifyContent: 'flex-start',
      mt: '16px'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    mt: '24px'
  },
  generateCheck: {
    '@media screen and (min-width:480px)': { display: 'none' }
  },
  hover: {
    '&:hover': {
      cursor: 'pointer',
      opacity: 0.7
    }
  },
  issueStatusTag: {
    '.MuiChip-label': {
      padding: '3px 16px'
    },
    backgroundColor: '#F3F3F3',
    border: '1px solid #757575',
    borderRadius: '4px',
    color: '#757575',
    fontSize: '12px',
    height: '24px',
    ml: '12px'
  },
  label: {
    backgroundColor: 'rgba(10, 126, 191, 0.08)',
    border: '1px solid ##0A7EBF',
    borderRadius: '4px',
    height: '24px',
    mb: '12px'
  },
  media: {
    height: '133px'
  },
  mediaSm: {
    '@media screen and (max-width:480px)': {
      height: '137px'
    },
    height: '10vh',
    mb: '16px',
    width: '100%'
  },
  secondaryButtonnBackground,
  star: {
    color: '#808A9F',
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
    textAlign: 'right',
    width: '100%'
  },
  tag: {
    '.MuiChip-label': {
      padding: '3px 16px'
    },
    backgroundColor: black,
    borderRadius: '4px',
    color: '#FFF',
    fontSize: '12px',
    height: '24px',
    width: '100%'
  },
  tagTitle: {
    '@media screen and (max-width:960px)': {
      margin: '24px 0 12px'
    },
    color: black,
    fontSize: '14px',
    fontWeight: 700,
    mb: '12px',
    ml: '8px',
    mt: '16px'
  },
  tags: {
    columnGap: '8px',
    rowGap: '8px'
  },
  text: {
    '@media screen and (max-width:480px)': { fontSize: '12px' },
    color: black,
    fontSize: '14px',
    overflowWrap: 'anywhere'
  }
};
const MypageIssueCard = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { issue, screen, companyStatus } = props;
  const { currentUser } = useContext(AuthContext);

  const [issueImage, setIssueImage] = useState<string>(noImage);

  const purposeList = purpose();
  const getPurpose = (value: number | null | undefined): string | undefined => {
    const purposeData = purposeList?.find((element: { value: number }) =>
      element.value === value ? element : null
    );
    return purposeData?.option;
  };

  const [clientName, setClientName] = useState<string>('');

  async function getNegotiationsByIssueId(): Promise<DocumentData[]> {
    const q = query(
      collection(db, 'negotiations'),
      where('issueId', '==', issue?.issueId)
    );
    const querySnapshot = await getDocs(q);
    const negotiations: DocumentData[] = [];
    querySnapshot.forEach((document) => {
      negotiations.push(document.data());
    });
    return negotiations;
  }

  const [statusCountValues, setStatusCountValues] = useState<{
    all: number;
    decline: number;
    inquiries: number;
    invoice: number;
    like: number;
    likeApproved: number;
    likeRejected: number;
    negotiation: number;
    order: number;
    orderApproval: number;
    posted: number;
    preparation: number;
    proposalPreparation: number;
  }>({
    all: 0,
    decline: 0,
    inquiries: 0,
    invoice: 0,
    like: 0,
    likeApproved: 0,
    likeRejected: 0,
    negotiation: 0,
    order: 0,
    orderApproval: 0,
    posted: 0,
    preparation: 0,
    proposalPreparation: 0
  });

  const init = async (): Promise<void> => {
    const companyId = issue?.companyId;
    const q = query(companiesRef, where('companyId', '==', companyId));
    const companySnap = await getDocs(q);
    setClientName(companySnap.docs[0].data().name as string);
    const image = await getIssueImage(
      (companySnap.docs[0].data().logoId as string) ?? ''
    );
    setIssueImage(image);

    const statusCounts: StatusCounts = {};
    let total = 0;
    const negotiations = await getNegotiationsByIssueId();
    negotiations.forEach((negotiation) => {
      const negoStatus = negotiation.status as number;
      if (statusCounts[negoStatus]) {
        statusCounts[negoStatus] += 1;
      } else {
        statusCounts[negoStatus] = 1;
      }
      total += 1;
    });
    setStatusCountValues({
      all: total,
      decline: statusCounts[negotiationStatus.decline] ?? 0,
      inquiries: statusCounts[negotiationStatus.inquiries] ?? 0,
      invoice: statusCounts[negotiationStatus.invoice] ?? 0,
      like: statusCounts[negotiationStatus.like] ?? 0,
      likeApproved: statusCounts[negotiationStatus.likeApproved] ?? 0,
      likeRejected: statusCounts[negotiationStatus.likeRejected] ?? 0,
      negotiation: statusCounts[negotiationStatus.negotiation] ?? 0,
      order: statusCounts[negotiationStatus.order] ?? 0,
      orderApproval: statusCounts[negotiationStatus.orderApproval] ?? 0,
      posted: statusCounts[negotiationStatus.posted] ?? 0,
      preparation: statusCounts[negotiationStatus.preparation] ?? 0,
      proposalPreparation:
        statusCounts[negotiationStatus.proposalPreparation] ?? 0
    });
  };

  useEffect(() => {
    void (async () => {
      await init();
    })();
  }, []);

  const [favoriteCount, setFavoriteCount] = useState<number>();

  useEffect(() => {
    try {
      if (currentUser && issue.issueId) {
        const issueRef = doc(issuesRef, issue.issueId);
        setFavoriteCount((issue.favoriteCount as number) ?? 0);

        // お気に入り数のリアルタイム更新処理
        const unsubscribe = onSnapshot(issueRef, (snap) => {
          const realtimeCount = snap.data()?.favoriteCount as number;
          setFavoriteCount(realtimeCount ?? 0);
        });

        return () => unsubscribe();
      }
    } catch (e) {
      throwError(e);
    }
  }, [issue.issueId]);

  return (
    <Grid
      sx={[
        styles.cardWrapper,
        ...(screen === screenType.agent ? [styles.hover] : [])
      ]}
      onClick={() => {
        if (screen === screenType.agent) {
          navigate(`/agent/mypage/issue/${issue.issueId}`);
        }
      }}
    >
      <Grid sx={{ flex: 1 }}>
        <Card
          sx={{
            '@media screen and (max-width:960px)': { display: 'block' },
            border: '1px solid #DBDBE0',
            boxShadow: 'none',
            display: 'flex'
          }}
        >
          <Typography component="div" sx={{ width: '30%' }}>
            <Grid
              sx={{
                '@media screen and (max-width:960px)': { display: 'none' },
                display: 'block'
              }}
            >
              <CardContent sx={styles.card}>
                <Grid container>
                  <Grid item xs={12}>
                    <img
                      src={issueImage}
                      style={{
                        height: 'auto',
                        objectFit: 'contain',
                        width: '100%'
                      }}
                      alt="issueImage"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography component="p" sx={styles.tagTitle}>
                          {t('issue.mediaInTransaction')}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container sx={styles.tags}>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?freeWord=${issue.name}`
                            );
                          }}
                          label={`すべて(${statusCountValues.all})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=inquiries&freeWord=${issue.name}`
                            );
                          }}
                          label={`お問い合わせ(${statusCountValues.inquiries})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=like&freeWord=${issue.name}`
                            );
                          }}
                          label={`いいね受取済 (${statusCountValues.like})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=likeApproved&freeWord=${issue.name}`
                            );
                          }}
                          label={`いいね承認済 (${statusCountValues.likeApproved})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=proposalPreparation&freeWord=${issue.name}`
                            );
                          }}
                          label={`提案依頼中 (${statusCountValues.proposalPreparation})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=negotiation&freeWord=${issue.name}`
                            );
                          }}
                          label={`商談中 (${statusCountValues.negotiation})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=order&freeWord=${issue.name}`
                            );
                          }}
                          label={`発注 (${statusCountValues.order})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=orderApproval&freeWord=${issue.name}`
                            );
                          }}
                          label={`発注承認(${statusCountValues.orderApproval})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=likeRejected&freeWord=${issue.name}`
                            );
                          }}
                          label={`いいね拒否(${statusCountValues.likeRejected})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=decline&freeWord=${issue.name}`
                            );
                          }}
                          label={`辞退(${statusCountValues.decline})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=preparation&freeWord=${issue.name}`
                            );
                          }}
                          label={`掲載準備中(${statusCountValues.preparation})`}
                          sx={styles.tag}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          onClick={() => {
                            navigate(
                              `/client/mypage/message?type=posted&freeWord=${issue.name}`
                            );
                          }}
                          label={`掲載済(${statusCountValues.posted})`}
                          sx={styles.tag}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Grid>
          </Typography>
          <Grid
            sx={{
              '@media screen and (max-width:960px)': { display: 'block' },
              display: 'none'
            }}
          >
            <CardContent sx={styles.cardcontent}>
              <Grid container>
                <Grid item xs={12} sm={12} md={12}>
                  <img
                    src={issueImage}
                    style={{
                      height: 'auto',
                      objectFit: 'contain',
                      width: '100%'
                    }}
                    alt="issueImage"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <Typography component="div" sx={{ display: 'flex' }}>
                    <Typography
                      component="p"
                      sx={{ fontSize: '16px', width: '100%' }}
                    >
                      {clientName}
                    </Typography>
                    <Typography component="div" sx={styles.star}>
                      <StarBorderIcon />
                      <Typography component="span" sx={{ ml: '4px' }}>
                        {favoriteCount}
                      </Typography>
                    </Typography>
                  </Typography>
                  <Grid container flexDirection={'column'}>
                    <Typography
                      component="p"
                      sx={[styles.categoryTitle, { mt: '16px' }]}
                    >
                      {issue.name}
                    </Typography>
                    {companyStatus === status.approved && (
                      <Chip
                        label={
                          issue.status === status.approved
                            ? t('status.approved')
                            : issue.status === status.notChecked
                            ? t('status.notChecked')
                            : issue.status === status.fix
                            ? t('status.fix')
                            : t('status.deleted')
                        }
                        sx={[
                          styles.issueStatusTag,
                          { ml: 0, mt: '16px', width: '95px' }
                        ]}
                      />
                    )}
                    {companyStatus !== status.approved && (
                      <Chip
                        label={
                          companyStatus === status.notChecked
                            ? t('status.notChecked')
                            : companyStatus === status.fix
                            ? t('status.fix')
                            : t('status.deleted')
                        }
                        sx={[
                          styles.issueStatusTag,
                          { ml: 0, mt: '16px', width: '95px' }
                        ]}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography component="p" sx={styles.tagTitle}>
                        {t('issue.mediaInTransaction')}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container sx={styles.tags}>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?freeWord=${issue.name}`
                          );
                        }}
                        label={`すべて(${statusCountValues.all})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=inquiries&freeWord=${issue.name}`
                          );
                        }}
                        label={`お問い合わせ(${statusCountValues.inquiries})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=like&freeWord=${issue.name}`
                          );
                        }}
                        label={`いいね受取済 (${statusCountValues.like})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=likeApproved&freeWord=${issue.name}`
                          );
                        }}
                        label={`いいね承認済 (${statusCountValues.likeApproved})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=proposalPreparation&freeWord=${issue.name}`
                          );
                        }}
                        label={`提案依頼中 (${statusCountValues.proposalPreparation})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=negotiation&freeWord=${issue.name}`
                          );
                        }}
                        label={`商談中 (${statusCountValues.negotiation})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=order&freeWord=${issue.name}`
                          );
                        }}
                        label={`発注 (${statusCountValues.order})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=orderApproval&freeWord=${issue.name}`
                          );
                        }}
                        label={`発注承認(${statusCountValues.orderApproval})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=likeRejected&freeWord=${issue.name}`
                          );
                        }}
                        label={`いいね拒否(${statusCountValues.likeRejected})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=decline&freeWord=${issue.name}`
                          );
                        }}
                        label={`辞退(${statusCountValues.decline})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=preparation&freeWord=${issue.name}`
                          );
                        }}
                        label={`掲載準備中(${statusCountValues.preparation})`}
                        sx={styles.tag}
                      />
                    </Grid>
                    <Grid item>
                      <Chip
                        onClick={() => {
                          navigate(
                            `/client/mypage/message?type=posted&freeWord=${issue.name}`
                          );
                        }}
                        label={`掲載済(${statusCountValues.posted})`}
                        sx={styles.tag}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Grid>
          <Grid sx={{ flex: 1 }}>
            <CardContent sx={styles.card}>
              <Typography component="div">
                <Typography
                  component="div"
                  sx={{
                    '@media screen and (max-width:960px)': { display: 'none' },
                    display: 'block'
                  }}
                >
                  <Grid>
                    <Grid container justifyContent="space-between">
                      <Grid item>{clientName}</Grid>
                      <Grid
                        item
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <Typography component="div" sx={styles.star}>
                          <StarBorderIcon />
                          <Typography component="span" sx={{ ml: '4px' }}>
                            {favoriteCount}
                          </Typography>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    mt={'16px'}
                  >
                    <Typography component="p" sx={styles.categoryTitle}>
                      {issue.name}
                    </Typography>
                    {companyStatus === status.approved && (
                      <Chip
                        label={
                          issue.status === status.approved
                            ? t('status.approved')
                            : issue.status === status.notChecked
                            ? t('status.notChecked')
                            : issue.status === status.fix
                            ? t('status.fix')
                            : t('status.deleted')
                        }
                        sx={styles.issueStatusTag}
                      />
                    )}
                    {companyStatus !== status.approved && (
                      <Chip
                        label={
                          companyStatus === status.notChecked
                            ? t('status.notChecked')
                            : companyStatus === status.fix
                            ? t('status.fix')
                            : t('status.deleted')
                        }
                        sx={styles.issueStatusTag}
                      />
                    )}
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.name')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      {issue.productName}
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.season')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      <Typography sx={styles.text}>
                        {issue.startDate
                          ? dayjs(issue?.startDate?.toDate()).format(
                              'YYYY/MM/DD'
                            )
                          : t('issue.consultation')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('issue.applicationDate')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      <Typography sx={styles.text}>
                        {issue?.applicationDate?.start &&
                          dayjs(issue.applicationDate?.start?.toDate()).format(
                            'YYYY/MM/DD'
                          )}{' '}
                        {issue?.applicationDate?.start && '~'}{' '}
                        {issue?.applicationDate?.end &&
                          dayjs(issue.applicationDate?.end?.toDate()).format(
                            'YYYY/MM/DD'
                          )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.purpose')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      <Typography sx={styles.text}>
                        {issue?.purpose && issue?.purpose[0] !== select.other
                          ? getPurpose(issue?.purpose[0])
                          : issue?.purposeOther}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      },
                      flexDirection: 'row'
                    }}
                  >
                    <Grid item xs={12} sx={[styles.editWrapper]}>
                      <Button
                        sx={styles.edit}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (screen === screenType.client) {
                            navigate(
                              `/client/mypage/issue/edit?type=edit&id=${issue.issueId}`
                            );
                          } else if (screen === screenType.agent) {
                            navigate(
                              `/agent/mypage/issue/edit?type=edit&id=${issue.issueId}`
                            );
                          }
                        }}
                      >
                        {t('given.edit')}
                      </Button>
                      {screen === screenType.agent && (
                        <Button
                          variant="outlined"
                          color="error"
                          sx={[
                            styles.secondaryButtonnBackground,
                            styles.copyButton
                          ]}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/agent/mypage/issue/edit?type=copy&copyId=${issue.issueId}`,
                              {
                                state: { id: issue.companyId }
                              }
                            );
                          }}
                        >
                          {t('given.copy')}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Typography>
              </Typography>
            </CardContent>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default MypageIssueCard;
